import { elements, inputs } from '@ecosystem/newron-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { CustomNumericalClass } from '../../../types/content/custom.types';
import constants from '../../constants';
import { getValidInputWithinRange, isClassDuplicate } from '../../utils';
import { CustomClassLabel } from '../customClassLabel';

const { NumberInput } = inputs;
const { Icon } = elements;

const { CLASS_EXISTS, MAX_CUSTOM_CLASS_TIER_VALUE, MIN_CUSTOM_CLASS_TIER_VALUE } = constants;

interface CustomFeatureClassesProps {
  index: number;
  featureClasses: CustomNumericalClass[]; //to check duplicate
  onFeatureChange: (index: number) => (value: number) => void;
  onLabelChange: (index: number) => (label: string) => void;
  errorMessage?: string;
}
export const CustomFeatureClasses: React.FC<CustomFeatureClassesProps> = ({
  index,
  featureClasses,
  onFeatureChange,
  onLabelChange,
  errorMessage,
}: CustomFeatureClassesProps) => {
  const classToRender = featureClasses[index];
  const [maxValue, setMaxValue] = useState<number>(classToRender.max);
  const [minValue, setMinValue] = useState<number>(classToRender.min);
  const [hasError, setHasError] = useState<boolean>(false);
  const [classErrorMessage, setClassErrorMessage] = useState<string>('');
  const featureOnChangeFn = onFeatureChange(index);

  useEffect(() => {
    //on re-rendering of customClasses if duplicate exist update error state.
    updateClassErrorState(featureClasses);

    setMaxValue(Number(classToRender.max?.toFixed(2))); // Update maxValue when classToRender.max changes
    setMinValue(Number(classToRender.min?.toFixed(2))); // Update minValue when classToRender.min changes
  }, [featureClasses, classToRender]);

  /**
   * Function to check if the input class has errors exists while re-rendering custom classes.
   * @param customClasses the input
   */
  const updateClassErrorState = useCallback(
    (customClasses: CustomNumericalClass[]) => {
      setClassErrorMessage('');

      // checked this way as if (classToRender) will return false for 0
      if (classToRender.max !== undefined && classToRender.max !== null) {
        const duplicateClassExist = isClassDuplicate(classToRender, customClasses);
        if (duplicateClassExist) {
          setClassErrorMessage(CLASS_EXISTS);
          setHasError(true);
          return;
        }
        if (classToRender.max < classToRender.min) {
          setHasError(true);
          return;
        }
        setHasError(false);
        setClassErrorMessage('');
      }
    },
    [classToRender]
  );

  const handleRangeChange = (input: number): void => {
    const validInputWithinRange = getValidInputWithinRange(input);

    //Here we will check if the input is within the min and max allowed custom tier
    if (input < validInputWithinRange) {
      setHasError(true);
      setMaxValue(input);
      return;
    }

    setMaxValue(validInputWithinRange);
  };

  const handleRangeBlur = (inputMin: number, inputMax: number): void => {
    // we aren't using !input as this would return true if input was 0
    if (inputMax === undefined || inputMax === null) return;

    // if the input is less than minValue, we set the error
    if (inputMax < minValue) {
      setHasError(true);
      featureOnChangeFn(inputMax);
      return;
    }

    // check for duplicate class
    if (isClassDuplicate({ min: inputMin, max: inputMax, label: '' }, featureClasses)) {
      setClassErrorMessage(CLASS_EXISTS);
      setHasError(true);
      featureOnChangeFn(inputMax);
      return;
    }

    setClassErrorMessage('');
    setHasError(false);
    featureOnChangeFn(inputMax);
  };

  /**
   * Function to return what to render in the higher value input, either a infinite sign or a number input
   * @returns JSX.Element
   */
  const renderHigherValue = () => {
    if (index === featureClasses.length - 1) {
      return (
        <div className="pl-8 pt-2">
          <Icon color="inactive" size="lg" testId="infinite-icon">
            <Icon.icons.Infinite />
          </Icon>
        </div>
      );
    }

    return (
      <div className="w-2/4">
        <NumberInput
          key={`max-feature-${index}`}
          value={classToRender.max}
          onChange={input => handleRangeChange(input)}
          onBlur={() => handleRangeBlur(minValue, maxValue)}
          testId={`max-feature-${index}`}
          hasError={hasError}
          errorMessage={classErrorMessage}
          minValue={MIN_CUSTOM_CLASS_TIER_VALUE}
          maxValue={MAX_CUSTOM_CLASS_TIER_VALUE}
          disabled={index === featureClasses.length - 1}
        />
      </div>
    );
  };
  return (
    <div className="pt-5">
      <div className="relative pb-2" data-test-id={'feature'}>
        <div className="flex items-start gap-2">
          <div className="w-2/4">
            <NumberInput
              key={`min-feature-${index}`}
              value={classToRender.min}
              disabled={true}
              onChange={() => {}}
              testId={`min-feature-${index}`}
              minValue={MIN_CUSTOM_CLASS_TIER_VALUE}
              maxValue={MAX_CUSTOM_CLASS_TIER_VALUE}
            />
          </div>

          <span className="pt-3">—</span>
          {renderHigherValue()}
        </div>
      </div>
      <CustomClassLabel
        customClasses={featureClasses}
        labelOnChangeFn={onLabelChange(index)}
        index={index}
        isLabelOptional={true}
        errorMessage={errorMessage}
      />
    </div>
  );
};
